import { storeToRefs } from 'pinia'
import useLoggedUserStore from '../stores/useLoggedUserStore'
import { resolveRoutePermissionRule, type RoutePermissionRule } from './useRoutePermissionMap'
import { UserPermissions } from '~/domain/dashboard/user/permissions/models/UserPermision'

function evaluateRoutePermissionRule (
  rule: RoutePermissionRule,
  permissionSet: Set<string>
): boolean {
  if (typeof rule === 'string') {
    return permissionSet.has(rule)
  }

  if ('any' in rule) {
    return rule.any.some(permission => permissionSet.has(permission))
  }

  return rule.all.every(permission => permissionSet.has(permission))
}

function resolveMissingPermissions (
  rule: RoutePermissionRule,
  permissionSet: Set<string>
): UserPermissions[] {
  if (typeof rule === 'string') {
    return permissionSet.has(rule) ? [] : [rule]
  }

  if ('any' in rule) {
    if (rule.any.some(permission => permissionSet.has(permission))) {
      return []
    }

    return [...rule.any]
  }

  return rule.all.filter(permission => !permissionSet.has(permission))
}

type ProfileHasPermission = {
  hasPermission: (permission: UserPermissions) => boolean
  hasPermissions: (rule: RoutePermissionRule) => boolean
  canAccessRoute: (path: string) => boolean
}

export function useProfileHasPermission (): ProfileHasPermission {
  const store = useLoggedUserStore()
  const { permissionSet } = storeToRefs(store)

  function hasPermission (permission: UserPermissions): boolean {
    return permissionSet.value.has(permission)
  }

  function hasPermissions (rule: RoutePermissionRule): boolean {
    return evaluateRoutePermissionRule(rule, permissionSet.value)
  }

  function canAccessRoute (path: string): boolean {
    const rule = resolveRoutePermissionRule(path)
    if (rule == null) {
      return true
    }

    return evaluateRoutePermissionRule(rule, permissionSet.value)
  }

  return { hasPermission, hasPermissions, canAccessRoute }
}

export { evaluateRoutePermissionRule, resolveMissingPermissions }
